import { observer } from "mobx-react-lite";
import logoText from "../../resources/logo-text.png";
import React, { useState } from "react";
import useTranslation from "../../hooks/useTranslation";
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import "./Header.scss";
import UserMenu from "./UserMenu/UserMenu";
import { Search, Menu } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import authStore from "../../stores/AuthStore";
import { UserRoles } from "../../stores/AuthStore/types";
import { debounce } from "lodash";
import caseStore from "../../stores/CaseStore";
import Dropdown from "../Dropdown";
import configStore from "../../stores/ConfigStore";
import { languageOptions } from "../../languages";
import { DropdownItem } from "../Dropdown/types";
import { AppLang } from "../../stores/ConfigStore/types";
import DrawerMenu from "./DrawerMenu";
import { CaseStatus } from "../../stores/CaseStore/types";
import ReactGA from "react-ga4";

const Header = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchClasses, setSearchClasses] = useState(["searchContainer"]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isLawyer = authStore.userHasRole(UserRoles.LAWYER);
  const activeCasesCount = caseStore.userCases.filter(
    (caseItem) => caseItem.status !== CaseStatus.COMPLETED
  ).length;
  const searchInputProps = {
    endAdornment: (
      <InputAdornment position={"end"}>
        <Search />
      </InputAdornment>
    ),
  };

  const handleSearchClick = () => {
    navigate(`/search/${UserRoles.USER}`);
    document.getElementById("searchTextField")?.focus();

    ReactGA.event({
      category: "User Interaction",
      action: "Search Click",
    });
  };

  const handleSearchTextChange = debounce((keyword) => {
    caseStore.setSearchKeyword(keyword);
  }, 500);

  const handleNewCaseClick = () => {
    isLawyer ? navigate("/delegation/new") : navigate("/case/new");

    ReactGA.event({
      category: "User Interaction",
      action: "New Case Click",
      label: "New Case",
    });
  };

  const handleOnLanguageSelected = (item: DropdownItem) => {
    configStore.setCurrentLanguage(item.key as AppLang);
  };

  return (
    <div className="headerContainer">
      <nav>
        <div>
          <div className="logoContainer">
            <IconButton
              onClick={() => setDrawerOpen(true)}
              className="hidden-lg"
            >
              <Menu />
            </IconButton>
            <Link to={"/"}>
              <img src={logoText} alt="logo" />
            </Link>
          </div>
        </div>
        {authStore.userHasRole(UserRoles.LAWYER) && (
          <div className={searchClasses.join(" ")}>
            <TextField
              id="searchTextField"
              onClick={handleSearchClick}
              onChange={(e) => handleSearchTextChange(e.target.value)}
              onFocus={() => setSearchClasses([...searchClasses, "focused"])}
              onBlur={() =>
                setSearchClasses(
                  [...searchClasses].filter(
                    (className) => className !== "focused"
                  )
                )
              }
              label={t("search-post")}
              variant="standard"
              size={"small"}
              InputProps={searchInputProps}
            />
          </div>
        )}
        <div>
          <div className="languagePicker">
            <Dropdown
              activeKey={configStore.currentLanguage}
              options={languageOptions}
              onItemSelected={handleOnLanguageSelected}
            />
          </div>
          <div className={"ml hidden-sm"} />
          {(authStore.userHasRole(UserRoles.USER) ||
            authStore.userHasRole(UserRoles.LAWYER)) && (
            <>
              <Tooltip
                title={
                  activeCasesCount >= configStore.appConfig.caseLimit
                    ? t("your-case-limit", [
                        `${configStore.appConfig.caseLimit}`,
                      ])
                    : ""
                }
                placement={"top-start"}
              >
                <Button
                  color={isLawyer ? "error" : "info"}
                  variant={"contained"}
                  className={"hidden-sm"}
                  onClick={
                    activeCasesCount < configStore.appConfig.caseLimit
                      ? handleNewCaseClick
                      : () => {}
                  }
                >
                  <span>{isLawyer ? t("delegation") : t("new-case")}</span>
                </Button>
              </Tooltip>
              <div className={"ml hidden-sm"} />
            </>
          )}
          <div className="hidden-sm">
            <UserMenu />
          </div>
          <div className="hidden-lg">
            <DrawerMenu open={drawerOpen} setOpen={setDrawerOpen} />
          </div>
        </div>
      </nav>
    </div>
  );
});

export default Header;
